import React, { useState, useEffect } from "react";
import { 
  Card, 
  CardBody, 
  CardImg, 
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Alert
} from "reactstrap";
import { connect, useDispatch } from "react-redux";
import { contactsUpdate } from "../../../store/contacts/actions";
import { getContactsRead } from "../../../helpers/backend";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SignatureModal from "../../../components/SignatureModal";
import avatarHombre from "../../../assets/images/avatarHombre.jpg";
import avatarMujer from "../../../assets/images/avatarMujer.jpg";
import companyAvatar from "../../../assets/images/companyAvatar.jpg";
import "./contact-sidebar.css";

const ContactSidebar = ({ 
  contactsUpdate,
  contactsUpdated,
  error
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [contact, setContact] = useState(null);
  const [formData, setFormData] = useState({
    full_name: "",
    razon_social: "",
    documentType: "",
    documentNumber: "",
    gender: "",
    birthdate: "",
    contactType: "",
    codigoCAUAGENT: "",
    codigo_visitador: "",
    cargo: "",
    firma: null
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureFile, setSignatureFile] = useState(null);

  // Cargar datos del contacto
  const getContactData = async () => {
    try {
      setLoading(true);
      const response = await getContactsRead(id);
      setContact(response.data);
      setFormData({
        full_name: response.data.full_name || "",
        razon_social: response.data.razon_social || "",
        documentType: response.data.documentType || "",
        documentNumber: response.data.documentNumber || "",
        gender: response.data.gender || "Otro",
        birthdate: response.data.birthdate || "",
        contactType: response.data.contactType || "",
        codigoCAUAGENT: response.data.codigoCAUAGENT || "",
        codigo_visitador: response.data.codigo_visitador || "",
        cargo: response.data.cargo || "",
        firma: response.data.firma || null
      });
      
      // Si hay una firma guardada, establecer el archivo de firma
      if (response.data.firma) {
        setSignatureFile(response.data.firma);
      }
    } catch (err) {
      console.error("Error fetching contact data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getContactData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Mostrar alerta de éxito
  useEffect(() => {
    if (contactsUpdated && contactsUpdated.id) {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setIsEditing(false);
        getContactData(); // Recargar datos después de actualizar
      }, 2000);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsUpdated]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSignatureSave = (signatureFile) => {
    setSignatureFile(signatureFile);
    setFormData(prev => ({
      ...prev,
      firma: signatureFile
    }));
    setShowSignatureModal(false);
    toast.success("Firma guardada correctamente");
  };

  const handleRemoveSignature = () => {
    setSignatureFile(null);
    setFormData(prev => ({
      ...prev,
      firma: null
    }));
    toast.info("Firma eliminada");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    if (signatureFile) {
      submitData.firma = signatureFile;
    }
    dispatch(contactsUpdate(id, submitData));
  };

  const getAvatar = () => {
    if (!contact) return avatarHombre;
    if (contact.image) return contact.image;
    if (contact.contactType === "EMPRESA") return companyAvatar;
    return contact.gender === "Femenino" ? avatarMujer : avatarHombre;
  };

  const renderStatusBadge = () => {
    if (!contact) return null;
    
    let color = "secondary";
    if (contact.status === "ACTIVO") color = "success";
    if (contact.status === "DESCONECTADO") color = "danger";
    
    return (
      <Badge color={color} pill className="status-badge">
        {contact.status || "SIN ESTADO"}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card className="contact-sidebar">
        <CardBody className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!contact) {
    return (
      <Card className="contact-sidebar">
        <CardBody className="text-center py-5">
          <p>No se pudo cargar la información del contacto</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="contact-sidebar">
      <CardBody>
        {/* Alertas */}
        {showSuccess && (
          <Alert color="success" className="alert-sm">
            ¡Los cambios se guardaron correctamente!
          </Alert>
        )}
        
        {error && (
          <Alert color="danger" className="alert-sm">
            Error al guardar los cambios
          </Alert>
        )}

        <div className="sidebar-header">
          <div className="avatar-container">
            <CardImg
              src={getAvatar()}
              alt="Profile"
              className="profile-avatar"
            />
            {renderStatusBadge()}
          </div>
          
          <h4 className="profile-name">
            {contact.full_name}
          </h4>
          
          <div className="profile-id">
          Cuenta Contrato: {contact.cuenta_contrato_ciudad_limpia} <br />
          Cuenta Enel: {contact.cuenta_enel}<br />
          Suscriptor: {contact.suscriptor}
          {contact.address && contact.address.length > 0 && (
            <>
              <div style={{ marginBottom: '5px', fontSize: '0.72rem' }}>
                <span style={{ color: '#dc3545', fontWeight: '600' }}>Dirección:</span><br />
                {contact.address[0].address}
              </div>
              {contact.address[0].city && contact.address[0].state && (
                <div style={{ marginBottom: '3px', fontSize: '0.72rem' }}>
                  <span style={{ color: '#6f42c1', fontWeight: '600' }}>Ciudad:</span><br />
                  {contact.address[0].city}, {contact.address[0].state}
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </CardBody>
      
      <SignatureModal
        isOpen={showSignatureModal}
        onHide={() => setShowSignatureModal(false)}
        onSave={handleSignatureSave}
        title="Firma Digital del Contacto"
      />
    </Card>
  );
};

const mapStateToProps = (state) => {
  const { error, contactsUpdated } = state.Contacts;
  return { error, contactsUpdated };
};

export default connect(mapStateToProps, { contactsUpdate })(ContactSidebar);